import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { WizardProgress } from "@/components/patterns/WizardProgress";
import { cn } from "@/lib/utils";
import { SETUP_STEP_INDEX, type SetupStep } from "./ai-policy-options";

export function SetupStepPanel({
  activeStep,
  stepId,
  children,
}: {
  activeStep: SetupStep;
  stepId: SetupStep;
  children: React.ReactNode;
}) {
  const active = activeStep === stepId;
  const beforeActive = SETUP_STEP_INDEX[stepId] < SETUP_STEP_INDEX[activeStep];
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.toggleAttribute("inert", !active);
  }, [active]);

  return (
    <div
      ref={panelRef}
      aria-hidden={!active}
      data-setup-step={stepId}
      className={cn(
        "absolute inset-0 overflow-y-auto overscroll-contain transition-[opacity,translate,filter] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[opacity,translate,filter] motion-reduce:transform-none motion-reduce:blur-none motion-reduce:transition-none",
        active && "pointer-events-auto translate-x-0 opacity-100 blur-none",
        !active && beforeActive && "pointer-events-none -translate-x-2 opacity-0 blur-[3px]",
        !active && !beforeActive && "pointer-events-none translate-x-2 opacity-0 blur-[3px]",
      )}
    >
      {children}
    </div>
  );
}

export function SetupProgress({ step }: { step: SetupStep }) {
  const { t } = useTranslation("dashboard");
  const active = step === "policy" ? 1 : step === "review" ? 2 : 3;
  return (
    <WizardProgress
      currentStep={active}
      labels={[
        t("policyDialog.steps.policy"),
        t("policyDialog.steps.review"),
        t("policyDialog.steps.enable"),
      ]}
    />
  );
}
